import SquareIcon from "@mui/icons-material/Square";
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  ListItem,
  Radio,
  TextField,
  TypographyProps,
} from "@mui/material";
import lodash from "lodash";
import { ReactNode } from "react";
import { Option } from "../constants/common";
import { INPUT_FIELD_HEIGHT } from "../theme";
import ErrorHelper from "./ErrorWrapper";
import Flex, { FlexProps } from "./Flex";
import { inputLabel } from "./MaskInput";
import { useFieldLock } from "./ReadOnlyFields";
import { PRIMITIVE } from "./ds";

export type Value = string | null;
type OnSearch = (
  e: React.SyntheticEvent,
  v: string,
  reason: AutocompleteInputChangeReason
) => void;

export interface CustomSelectProps {
  options?: Option[];
  label?: string;
  flexProps?: FlexProps;
  typographyProps?: TypographyProps;
  placeholder?: string;
  optionWithCheckbox?: boolean;
  disableCloseOnSelect?: boolean;
  onChange?: (value: Value) => void;
  endIcon?: ReactNode;
  value?: Value;
  clearable?: boolean;
  required?: boolean;
  error?: string | boolean;
  withStatusBox?: boolean;
  onSearch?: OnSearch;
  /**
   * The field cannot be edited. Greyed out on an editable form; inside a
   * `ReadOnlyFields` region the value is presented instead.
   */
  readOnly?: boolean;
  /** Alias of `readOnly`, for call sites that read better as "disabled". */
  disabled?: boolean;
  handleBlur?: any;
  name?: string;
  fullHeight?: boolean;
  enableFilterOption?: boolean;
  gutterTop?: boolean;
  renderCustomLabel?: (opt: Option) => ReactNode;
  disableSortOptions?: boolean;
  disablePortal?: boolean;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  name,
  label,
  flexProps,
  placeholder = "",
  options,
  onChange,
  value,
  optionWithCheckbox = false,
  disableCloseOnSelect = false,
  clearable = true,
  endIcon,
  required,
  error,
  onSearch,
  withStatusBox = false,
  readOnly = false,
  disabled = false,
  handleBlur,
  fullHeight,
  gutterTop,
  enableFilterOption,
  renderCustomLabel,
  disableSortOptions,
  disablePortal = false,
}) => {
  const lock = useFieldLock(readOnly || disabled);

  const handleInputChange = (e: any, newInputValue: string, reason: string) => {
    if (reason === "input") {
      onSearch?.(e, newInputValue, reason);
    }
  };

  return (
    <form autoComplete="off">
      <Flex
        {...flexProps}
        flexDirection={flexProps?.flexDirection ?? "column"}
        height={"100%"}
      >
        {/* {label && (
        <Typography mb={"10px"} sx={DEFAULT_FONTSIZE}>
          {label}
          {required && (
            <Typography fontSize={"13px"} component={"span"} color={"error"}>
              {` *`}
            </Typography>
          )}
        </Typography>
      )} */}
        <ErrorHelper error={error}>
          <Autocomplete
            disablePortal={disablePortal}
            style={{
              height: fullHeight ? "100%" : INPUT_FIELD_HEIGHT,
              marginTop: gutterTop ? "30px" : undefined,
            }}
            sx={
              fullHeight
                ? {
                    "& .MuiOutlinedInput-root": { height: "100%" },
                    "& .MuiInputBase-input": { height: "100% !important" },
                  }
                : undefined
            }
            onBlur={handleBlur}
            disabled={lock.disabled}
            readOnly={lock.readOnly}
            // A presented select has no popup to open, so the dropdown arrow
            // would be an affordance that does nothing — drop it.
            forcePopupIcon={lock.readOnly ? false : undefined}
            options={
              (disableSortOptions
                ? options?.filter((a) => a?.value !== value)
                : options?.sort((a, b) => a?.label?.localeCompare(b?.label))
              )?.filter((a) => a?.value !== value) ?? []
            }
            fullWidth
            disableClearable={!clearable}
            disableCloseOnSelect={disableCloseOnSelect}
            getOptionLabel={(option) => option.label}
            popupIcon={endIcon}
            onInputChange={handleInputChange}
            isOptionEqualToValue={lodash.isEqual}
            onKeyDown={(event) => {
              if (event.key === "Enter") event.preventDefault();
            }}
            filterOptions={enableFilterOption ? (opt) => opt : undefined}
            // Todo need to optimize this (change backend accept value)
            value={options?.find((opt) => opt?.value === value) ?? null}
            // sx={{
            //   ...(fullHeight && {
            //     // "& .MuiOutlinedInput-root": {
            //     //   minHeight: "inherit",
            //     // },
            //   }),
            //   "& .MuiInputBase-input": {
            //     ...DEFAULT_FONTSIZE,
            //     height: fullHeight ? "inherit" : undefined,
            //   },
            //   // If we provides a custom icon - then disable default rotation behavior
            //   ...(endIcon && {
            //     "& .MuiAutocomplete-popupIndicator": {
            //       transform: "none",
            //     },
            //   }),
            // }}
            onChange={(_, value) => onChange?.(value?.value ?? null)}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <ListItem
                  key={key}
                  {...optionProps}
                  sx={{
                    fontSize: `${PRIMITIVE.typography.fontSize.body.b5}px`,
                  }}
                >
                  {optionWithCheckbox && (
                    <Radio
                      checked={option.value === value}
                      size="small"
                      sx={{ p: 0, mr: 1 }}
                    />
                  )}

                  {withStatusBox && (
                    <SquareIcon
                      sx={{
                        backgroundColor: option.color,
                        color: option.color,
                        marginRight: "5px",
                      }}
                    />
                  )}

                  {renderCustomLabel ? renderCustomLabel(option) : option.label}
                </ListItem>
              );
            }}
            renderInput={(params: any) => (
              <TextField
                label={inputLabel(label, required)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.preventDefault();
                }}
                name={name}
                sx={{
                  ...params?.sx,
                  fontSize: `${PRIMITIVE.typography.fontSize.body.b5}px`,
                }}
                {...params}
                error={!!error && error}
                placeholder={placeholder}
                slotProps={{
                  ...params.slotProps,
                  inputLabel: { shrink: true },
                }}
              />
            )}
          />
        </ErrorHelper>
        {/* {!!error && (
          <Text
            fontSize={INPUT_FIELD_TEXT_SIZE}
            variant="caption" color="error">
            {error}
          </Text>
        )} */}
      </Flex>
    </form>
  );
};

export default CustomSelect;
